import React, { useEffect, useState, useMemo } from 'react'
import { DutyStatusOverviewMetaProps, Event } from '../data/types'
import { API_BASE_URL } from '../data'
import { CheckboxGroup, LoadingSpinner } from '../components'
import { sortByName } from '../utils/sorting'

type Props = {
    meta: DutyStatusOverviewMetaProps
}

type DutyStatusStats = {
    accepted: number
    declined: number
    pending: number
}

export const DutyStatusOverview = ({ meta }: Props) => {

    const [loading, setLoading] = useState(false);
    const [globalError, setGlobalError] = useState<string | null>(null)

    const [events, setEvents] = useState<Event[]>([])
    const [selectedEventIds, setSelectedEventIds] = useState<number[]>([]);

    const [eventStats, setEventStats] = useState<Record<number, DutyStatusStats>>({});

    const [loadingStatsIds, setLoadingStatsIds] = useState<number[]>([]);

    // Fetch Events on Mount
    useEffect(() => {
        const fetchInitialEvents = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: "include" });
                if (!response.ok) throw new Error("Failed to fetch event details.");
                const rawData = await response.json();
                setEvents(rawData);
            }
            catch (err) {
                setGlobalError(err instanceof Error ? err.message : "Failed to fetch event information");
            }
            finally {
                setLoading(false);
            }
        }
        fetchInitialEvents();
    }, [])

    // Fetch Stats
    const fetchEventStats = async (eventId: number) => {
        // Prevent refetch if already loading or already have data
        if (eventStats[eventId] || loadingStatsIds.includes(eventId)) return;

        try {
            setLoadingStatsIds(prev => [...prev, eventId]);

            const response = await fetch(`${API_BASE_URL}/dashboard/stats/duty-status/${eventId}`, {
                credentials: "include"
            });

            if (!response.ok) throw new Error(`Failed`);

            const data: DutyStatusStats = await response.json();

            setEventStats(prev => ({ ...prev, [eventId]: data }));
        } catch (err) {
            console.error(`Error fetching stats for event ${eventId}`, err);
        } finally {
            setLoadingStatsIds(prev => prev.filter(id => id !== eventId));
        }
    }

    // Handle Checkbox Changes
    const handleEventChange = (value: string | number) => {
        const id = Number(value);

        setSelectedEventIds(prev => {
            const isSelected = prev.includes(id);
            if (isSelected) {
                setEventStats(prevStats => {
                    const copy = { ...prevStats };
                    delete copy[id];
                    return copy;
                });
                return prev.filter(item => item !== id);
            } else {
                fetchEventStats(id);
                return [...prev, id];
            }
        });
    }

    // Bulk Actions
    const handleSelectAll = () => {
        const allIds = events.map(e => e.id);
        allIds.forEach(id => fetchEventStats(id));
        setSelectedEventIds(allIds);
    };

    const handleDeselectAll = () => {
        setSelectedEventIds([]);
        setEventStats({});
    };

    // Prepare Table Data
    const { tableData, totals, isAnyRowLoading } = useMemo(() => {
        const data = [];
        let totalAccepted = 0;
        let totalRejected = 0;
        let totalPending = 0;
        let anyLoading = false;

        for (const id of selectedEventIds) {
            const eventInfo = events.find(e => e.id === id);
            const stats = eventStats[id];
            const isLoading = loadingStatsIds.includes(id);

            if (eventInfo) {
                if (isLoading) anyLoading = true;

                // Only sum up if data exists and isn't loading
                if (stats && !isLoading) {
                    totalAccepted += stats.accepted;
                    totalRejected += stats.declined;
                    totalPending += stats.pending;
                }

                data.push({
                    id: id,
                    name: eventInfo.name,
                    isLoading,
                    accepted: stats?.accepted || 0,
                    rejected: stats?.declined || 0,
                    pending: stats?.pending || 0
                });
            }
        }

        return {
            tableData: data,
            totals: { accepted: totalAccepted, rejected: totalRejected, pending: totalPending },
            isAnyRowLoading: anyLoading
        };
    }, [events, selectedEventIds, eventStats, loadingStatsIds]);

    const getPercent = (val: number, totalA: number, totalR: number, totalP: number) => {
        const total = totalA + totalR + totalP;
        if (total === 0) return 0;
        return Math.round((val / total) * 100);
    }

    return (
        <div className="flex-1 w-full max-w-full space-y-8">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{meta.title}</h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{meta.description}</p>
            </div>

            {loading && <LoadingSpinner meta={{ loadingText: meta.loadingText }} />}

            {globalError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">{meta.errorTitle}</h3>
                    <p className="text-red-600 dark:text-red-300">{globalError}</p>
                </div>
            )}

            {!loading && (
                <>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-end gap-3 mb-2 text-sm">
                            <button onClick={handleSelectAll} className="text-primary-600 hover:text-primary-700 font-medium">Select All</button>
                            <span className="text-gray-300">|</span>
                            <button onClick={handleDeselectAll} className="text-gray-500 hover:text-gray-700 font-medium">Deselect All</button>
                        </div>
                        <CheckboxGroup
                            label={meta.eventSelectionLabel}
                            options={sortByName(events).map(e => ({ id: e.id, label: e.name }))}
                            selectedValues={selectedEventIds}
                            onChange={handleEventChange}
                            columns={3}
                            bordered={false}
                        />
                    </div>

                    {selectedEventIds.length > 0 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                            {/* --- TABLE 1: RAW COUNTS --- */}
                            <div className="overflow-hidden rounded-lg shadow ring-1 ring-black ring-opacity-5">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-primary-600 text-white">
                                        <tr>
                                            <th className="py-3.5 ps-4 pe-3 text-start text-sm font-semibold sm:ps-6">Event</th>
                                            <th className="px-3 py-3.5 text-end text-sm font-semibold">Accepted</th>
                                            <th className="px-3 py-3.5 text-end text-sm font-semibold">Rejected</th>
                                            <th className="px-3 py-3.5 text-end text-sm font-semibold">Pending</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white dark:bg-gray-800 dark:divide-gray-700">
                                        {tableData.map((row) => (
                                            <tr key={row.id}>
                                                <td className="whitespace-nowrap py-3 ps-4 pe-3 text-sm font-bold text-gray-900 dark:text-gray-100 sm:ps-6">
                                                    {row.name}
                                                </td>
                                                {row.isLoading ? (
                                                    <td colSpan={3} className="px-3 py-3 text-xs text-center text-gray-500 italic animate-pulse">Loading stats...</td>
                                                ) : (
                                                    <>
                                                        <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">{row.accepted}</td>
                                                        <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">{row.rejected}</td>
                                                        <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">{row.pending}</td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                        {/* Overall Row */}
                                        <tr className="bg-primary-600/90 text-white italic">
                                            <td className="whitespace-nowrap py-3 ps-4 pe-3 text-sm font-bold sm:ps-6">Overall</td>
                                            {isAnyRowLoading ? (
                                                <td colSpan={3} className="px-3 py-3 text-xs text-center opacity-80">Calculating totals...</td>
                                            ) : (
                                                <>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">{totals.accepted}</td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">{totals.rejected}</td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">{totals.pending}</td>
                                                </>
                                            )}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* --- TABLE 2: PERCENTAGES --- */}
                            <div className="overflow-hidden rounded-lg shadow ring-1 ring-black ring-opacity-5">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-primary-600 text-white">
                                        <tr>
                                            <th className="py-3.5 ps-4 pe-3 text-start text-sm font-semibold sm:ps-6">Event</th>
                                            <th className="px-3 py-3.5 text-end text-sm font-semibold">Accepted %</th>
                                            <th className="px-3 py-3.5 text-end text-sm font-semibold">Rejected %</th>
                                            <th className="px-3 py-3.5 text-end text-sm font-semibold">Pending %</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white dark:bg-gray-800 dark:divide-gray-700">
                                        {tableData.map((row) => (
                                            <tr key={row.id}>
                                                <td className="whitespace-nowrap py-3 ps-4 pe-3 text-sm font-bold text-gray-900 dark:text-gray-100 sm:ps-6">
                                                    {row.name}
                                                </td>
                                                {row.isLoading ? (
                                                    <td colSpan={3} className="px-3 py-3 text-xs text-center text-gray-500 italic animate-pulse">Calculating...</td>
                                                ) : (
                                                    <>
                                                        <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">
                                                            {getPercent(row.accepted, row.accepted, row.rejected, row.pending)}%
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">
                                                            {getPercent(row.rejected, row.accepted, row.rejected, row.pending)}%
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">
                                                            {getPercent(row.pending, row.accepted, row.rejected, row.pending)}%
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                        {/* Overall Row */}
                                        <tr className="bg-primary-600/90 text-white italic">
                                            <td className="whitespace-nowrap py-3 ps-4 pe-3 text-sm font-bold sm:ps-6">Overall</td>
                                            {isAnyRowLoading ? (
                                                <td colSpan={3} className="px-3 py-3 text-xs text-center opacity-80">Calculating totals...</td>
                                            ) : (
                                                <>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">
                                                        {getPercent(totals.accepted, totals.accepted, totals.rejected, totals.pending)}%
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">
                                                        {getPercent(totals.rejected, totals.accepted, totals.rejected, totals.pending)}%
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">
                                                        {getPercent(totals.pending, totals.accepted, totals.rejected, totals.pending)}%
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                            <p className="text-gray-500 dark:text-gray-400">Please select at least one event to view the breakdown.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
